/** The entity-profile MERGE modal (person-merge spec §7, slice PM-9).
 *
 * ⭐ WHY IT EXISTS — two real operator errors in one session, both from selecting a record by TYPED
 * ID: `625` typed for `1625` armed a merge of the wrong company, and post-merge "which one
 * survived?" confusion ended in a hand-edit in prod (the one edit that forfeits the undo). Choosing
 * by identity — search, then read BOTH names back in the confirm button — removes the class.
 *
 * ⛔ IT DOES NOT WEAKEN THE RITUAL. The merge is still the shipped route: preview by default, an
 * evidence note required to write, and a typed confirmation. What changed is WHAT is typed: a value
 * that DISTINGUISHES the two records (the merged-away name, or its address when the names match)
 * rather than a number that merely identifies one of them. The rule is served by the preview
 * (`confirm_field`), never re-derived here — a browser copy of "when are two names the same" would be
 * a second normalizer beside the backend's.
 *
 * ⛔ AND IT NEVER FALLS BACK TO THE ID. A form that asks for a name, then an email, then a number
 * teaches the operator to type whatever it asks for, which is the habit the id ritual already failed
 * at. When neither value distinguishes the pair (a retracted shell: no name, no address) the modal
 * REFUSES to arm and names the maintenance tool, where the id is the whole ritual rather than a
 * fallback inside a friendlier one.
 *
 * Modal state is `useState` — transient modals are not URL state (frontend rules).
 */
import { useEffect, useMemo, useState } from 'react';
import styles from './crm.module.css';
import { ApiError } from '../../api/client';
import {
  useCrmMerge,
  useCrmMergeCandidates,
  type CrmEntityKind,
} from '../../hooks/useCrm';
import type { CrmMergeCandidate, CrmMergeConfirmField } from '../../types';

interface Props {
  kind: CrmEntityKind;
  /** The record whose profile is open. Either role — the operator picks. */
  id: number;
  name: string | null;
  onClose: () => void;
  /** Called after a merge commits, so the profile can navigate to the survivor. */
  onMerged: (survivorId: number) => void;
}

const label = (c: CrmMergeCandidate | undefined | null) =>
  (c?.name || '(unnamed)');

const detail = (c: CrmMergeCandidate | undefined | null) =>
  (c ? (c.primary_email || c.primary_domain || 'no address') : '');

/** Read the SERVER's reason out of an ApiError, including the structured refusals the merge routes
 *  raise.
 *
 *  ⛔ THE UNWRAP IS THE POINT. FastAPI wraps an HTTPException's payload as `{"detail": <payload>}`,
 *  so a route raising `HTTPException(409, {error, message, what_happened, next_step})` arrives here
 *  as `e.detail.detail.message` — one level deeper than this reader used to look. It checked
 *  `rec.detail` for a STRING, found an object, and fell through to `e.message`. That is how the
 *  StepStone merge (2026-08-27) showed the operator a bare `API error: 409` while the response
 *  carried the exact table, column and row the integrity guard had refused on — the precise failure
 *  the guard's own comment warns about, one layer further out.
 *
 *  `next_step` is joined on, not dropped: every structured refusal here says whether anything was
 *  written and whether a retry can succeed, which is the half that decides what the operator does.
 *
 *  EXPORTED (not copied) for the DEAL merge modal, whose routes raise the same FastAPI-wrapped
 *  structured refusals. A second copy of this unwrap is a second place for the StepStone bug to come
 *  back — the whole reason it reads three levels deep is that someone once assumed one. */
export const errText = (e: unknown, fb: string) => {
  if (!(e instanceof ApiError)) return fb;
  let d: unknown = e.detail;
  if (typeof d === 'string' && d) return d;
  // Descend through FastAPI's envelope. A bounded loop, not a recursion: `detail.detail` is one
  // level in practice, and a cycle here would hang the modal rather than show an error.
  for (let i = 0; i < 3 && d && typeof d === 'object' && !Array.isArray(d); i += 1) {
    const rec = d as Record<string, unknown>;
    const msg = rec.message ?? rec.error;
    if (typeof msg === 'string' && msg) {
      const next = typeof rec.next_step === 'string' ? rec.next_step : '';
      return next ? `${msg}

${next}` : msg;
    }
    if (typeof rec.detail === 'string' && rec.detail) return rec.detail;
    d = rec.detail;
  }
  return e.message || fb;
};

export function CrmMergeModal({ kind, id, name, onClose, onMerged }: Props) {
  const [q, setQ] = useState('');
  /** Debounced at 250ms, the house figure (`CrmRefCellEditor`, `CrmListGrid`, `CrmNoteOverlay`).
   *  Without it "alexandra" is nine round-trips, each of which reads the governed directory. */
  const [dq, setDq] = useState('');
  const [otherId, setOtherId] = useState<number | null>(null);
  /** Which record SURVIVES. Defaults to the profile we opened from — the least surprising direction
   *  — and the swap control makes the other choice explicit rather than implied by field order. */
  const [survivorIsSelf, setSurvivorIsSelf] = useState(true);
  const [note, setNote] = useState('');
  const [typed, setTyped] = useState('');
  const [ack, setAck] = useState(false);
  const [preview, setPreview] = useState<Record<string, unknown> | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [role, setRole] = useState('merge');

  const list = useCrmMergeCandidates(kind, id, dq, true);
  const merge = useCrmMerge(kind);

  const self = list.data?.self;
  const other = useMemo(
    () => list.data?.candidates.find((c) => c.id === otherId) ?? null,
    [list.data?.candidates, otherId]);

  const survivor = survivorIsSelf ? self : other;
  const legacy = survivorIsSelf ? other : self;

  // A LOGIN may be the SURVIVOR of a grant-free merge (R14b) but never the merged-away side. The
  // refusal is therefore about the ROLE, not the record — so it is recomputed on every swap rather
  // than baked into whether a candidate is selectable at all.
  const legacyIsLogin = legacy?.is_login === true;

  useEffect(() => { const t = setTimeout(() => setDq(q), 250); return () => clearTimeout(t); }, [q]);

  // Re-preview whenever the pair or its direction changes; a stale preview describing the other
  // direction is exactly the confusion this modal exists to remove.
  //
  // ⛔ `note` RESETS TOO, and leaving it out was a real defect (adversarial review finding 1): the
  // evidence note is free text about a SPECIFIC pair, it lands verbatim on `crm_merge_log`, and
  // `canApply` gates on it being non-empty. Carried over, it both pre-satisfied the one deliberate
  // free-text step and made the audited "why" describe a pair it was not written about — and on a
  // SWAP its meaning reverses ("the loser's address" now names the other record).
  useEffect(() => { setPreview(null); setTyped(''); setAck(false); setErr(null); setNote(''); },
    [otherId, survivorIsSelf]);

  // ⚠ CLAMP A SELECTION THAT FELL OUT OF THE RESULTS (review finding 10). Editing the query can drop
  // the selected record from the new result set, and `otherId` alone would keep pointing at it: the
  // direction panel unmounted while the preview kept rendering "41 references would move from
  // (unnamed) to (unnamed)". Nothing wrong could be SUBMITTED (`canApply` needs a `legacy`), but a
  // surface built to remove "which record was it?" was rendering exactly that.
  useEffect(() => {
    if (otherId == null || !list.data) return;
    if (!list.data.candidates.some((c) => c.id === otherId)) setOtherId(null);
  }, [list.data, otherId]);

  const confirmField: CrmMergeConfirmField | null =
    (preview?.confirm_field as CrmMergeConfirmField | undefined) ?? null;
  const staleIndex = preview?.stale_index === true || list.data?.index_is_behind === true;
  const warnings = (preview?.warnings as string[] | undefined) ?? [];
  const status = (preview?.status as string | undefined) ?? null;
  const namePlan = preview?.name_plan as
    { name_after?: string | null; loser_name_becomes_alias?: boolean } | undefined;

  const runPreview = () => {
    if (!survivor || !legacy) return;
    setErr(null);
    merge.mutate({ survivor_id: survivor.id, loser_id: legacy.id },
      { onSuccess: (out) => setPreview(out), onError: (e) => setErr(errText(e, 'Preview failed.')) });
  };

  const armable = !!confirmField?.field && !!confirmField.expected;
  const armed = armable && typed.trim() === (confirmField!.expected || '').trim();
  const needsAck = warnings.length > 0;
  const canApply = !!survivor && !!legacy && !legacyIsLogin && role === 'merge'
    && status === 'ready' && armed && !!note.trim() && (!needsAck || ack) && !merge.isPending;

  const apply = () => {
    if (!canApply || !survivor || !legacy) return;
    setErr(null);
    merge.mutate({
      survivor_id: survivor.id, loser_id: legacy.id, evidence_note: note.trim(),
      confirm_loser_id: legacy.id, acknowledge_governance_impact: ack,
    }, {
      onSuccess: (out) => {
        if (out?.applied === true) onMerged(survivor.id);
        else setPreview(out);
      },
      onError: (e) => setErr(errText(e, 'The merge was refused.')),
    });
  };

  return (
    <div className={styles.modalBackdrop} onMouseDown={onClose}>
      <div className={styles.mergeCard} role="dialog" aria-label="Merge records"
           onMouseDown={(e) => e.stopPropagation()}>
        <h3 className={styles.modalTitle}>
          {/* The record we opened from is named in the TITLE, from the profile's own value, so the
              dialog identifies itself before the counterpart search has returned anything. */}
          Merge {kind === 'person' ? 'people' : 'companies'} — {self?.name || name || '(unnamed)'}
        </h3>
        <div className={styles.mergeIntro}>
          One record absorbs the other. The record you keep gains the other's mail, meetings, notes
          and history; the other becomes a tombstone whose address still resolves here. Reversible,
          with limits — the preview says what would change before anything is written.
        </div>

        {/* R24's reserved slot. An inert control that is not RENDERED is a control an operator goes
            looking for in the merge instead. Rendered, disabled, and explained. */}
        <fieldset className={styles.mergeRoles}>
          <legend className={styles.mergeLabel}>What are you recording?</legend>
          {(list.data?.roles ?? []).map((r) => (
            <label key={r.key}
                   className={r.enabled ? styles.mergeRole : styles.mergeRoleDisabled}
                   title={r.why || undefined}>
              <input type="radio" name="crm-merge-role" value={r.key}
                     disabled={!r.enabled}
                     checked={role === r.key}
                     onChange={() => r.enabled && setRole(r.key)} />
              <span>{r.label}</span>
              {!r.enabled && <span className={styles.mergeRoleNote}>{r.why}</span>}
            </label>
          ))}
        </fieldset>

        <div className={styles.mergeLabel}>The other record</div>
        <input
          className={styles.modalInput}
          placeholder={`Search by name (at least 2 characters)`}
          value={q}
          autoFocus
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }}
        />
        {list.isError && <div className={styles.modalError} role="alert">Could not search.</div>}
        <div className={styles.mergeResults}>
          {(list.data?.candidates ?? []).length === 0 && (
            <div className={styles.mergeEmpty}>
              {dq.trim().length < 2
                ? 'Type a name, or pick a suggested duplicate if there is one.'
                : 'No matching records you can see.'}
            </div>
          )}
          {(list.data?.candidates ?? []).map((c) => (
            <button key={c.id} type="button"
                    className={c.id === otherId ? styles.mergeResultOn : styles.mergeResult}
                    onClick={() => setOtherId(c.id)}>
              <span className={styles.mergeResultName}>{label(c)}</span>
              <span className={styles.mergeResultMeta}>
                {detail(c)}
                {c.interactions_visible != null && ` · ${c.interactions_visible} interactions you can see`}
              </span>
              {c.suggested && (
                <span className={`${styles.chip} ${styles.chipDup}`}>
                  suggested{c.already_queued_basis ? ` · ${c.already_queued_basis}` : ''}
                </span>
              )}
              {c.is_login && (
                <span className={`${styles.chip} ${styles.chipMuted}`}>internal login</span>
              )}
              {/* ⚠ A DISTRIBUTION LIST OR SHARED MAILBOX IS NOT A PERSON, and nothing in the merge
                  engine refuses one (review finding 8). The search deliberately returns them — it only
                  sorts them last — and the old id-typing path meant you had already looked the record
                  up. A picker hands it to you beside the real people, so say what it is. */}
              {c.kind === 'person' && c.subtype != null && c.subtype !== 'person' && (
                <span className={`${styles.chip} ${styles.chipMuted}`}>
                  {c.subtype.replace(/_/g, ' ')} — not a person
                </span>
              )}
            </button>
          ))}
        </div>

        {other && (
          <>
            <div className={styles.mergeDirection}>
              <div>
                <div className={styles.mergeLabel}>Keeps existing</div>
                <div className={styles.mergeDirName}>{label(survivor)}</div>
                <div className={styles.mergeResultMeta}>{detail(survivor)}</div>
              </div>
              <button type="button" className={styles.gridMiniBtn}
                      onClick={() => setSurvivorIsSelf((v) => !v)}
                      aria-label="Swap which record survives">⇄ Swap</button>
              <div>
                <div className={styles.mergeLabel}>Merged away</div>
                <div className={styles.mergeDirName}>{label(legacy)}</div>
                <div className={styles.mergeResultMeta}>{detail(legacy)}</div>
              </div>
            </div>

            {legacyIsLogin && (
              <div className={styles.modalError} role="alert">
                {label(legacy)} is an internal Ember login, and a login can only ever be the record
                that <em>survives</em> — merging one away would take away the address its sign-in
                resolves through. Swap the direction, or attach the second address as an alias instead
                (Admin → Maintenance → “CRM — attach a second email address to a person”).
              </div>
            )}

            {staleIndex && (
              <div className={styles.mergeWarn}>
                Search is catching up on one of these records, so a name you saw in search results may
                be out of date. The names above come from the database itself.
              </div>
            )}

            {!preview && !legacyIsLogin && (
              <div className={styles.modalActions}>
                <button className={styles.gridPageBtn} disabled={merge.isPending}
                        onClick={runPreview}>
                  {merge.isPending ? 'Checking…' : 'Show me what would change'}
                </button>
              </div>
            )}
          </>
        )}

        {preview && (
          <div className={styles.mergePreview}>
            {/* ⛔ WARNINGS FIRST, before any count. The 08-25 incident was an operator acting on a
                surface that led with reassuring numbers. */}
            {warnings.map((w, i) => (
              <div key={i} className={styles.mergeWarn} role="alert">{w}</div>
            ))}
            {status !== 'ready' && (
              <div className={styles.modalError} role="alert">
                This merge cannot run: {status}.
              </div>
            )}
            <div className={styles.mergePreviewLine}>
              <strong>{String(preview.total_changes ?? 0)}</strong> references would move from{' '}
              {label(legacy)} to {label(survivor)}.
            </div>
            {namePlan && (
              <div className={styles.mergePreviewLine}>
                Afterwards this record is called <strong>{namePlan.name_after || '(unnamed)'}</strong>
                {namePlan.loser_name_becomes_alias
                  ? `, and “${label(legacy)}” is kept as a searchable alternative name.`
                  : '.'}
              </div>
            )}
            {(preview.login_exemption as unknown) != null && (
              <div className={styles.mergeWarn}>
                One side is an internal login and it is the record that survives. That is permitted
                only because the other record carries no permission of its own — which was checked,
                not assumed. Its mail history becomes readable by the surviving login.
              </div>
            )}

            {status === 'ready' && !armable && (
              <div className={styles.modalError} role="alert">{confirmField?.why || confirmField?.label}</div>
            )}
            {status === 'ready' && armable && (
              <>
                <label className={styles.mergeLabel} htmlFor="crm-merge-note">
                  Why are these one {kind === 'person' ? 'person' : 'company'}?
                </label>
                <input id="crm-merge-note" className={styles.modalInput} value={note}
                       placeholder="A shared address, a signature block, someone who confirmed it"
                       onChange={(e) => setNote(e.target.value)} />
                <label className={styles.mergeLabel} htmlFor="crm-merge-confirm">
                  {confirmField!.label}
                </label>
                {confirmField!.why && (
                  <div className={styles.mergeResultMeta}>{confirmField!.why}</div>
                )}
                <input id="crm-merge-confirm" className={styles.modalInput} value={typed}
                       autoComplete="off"
                       onChange={(e) => setTyped(e.target.value)} />
                {needsAck && (
                  <label className={styles.modalCheck}>
                    <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} />
                    I have read the warnings above
                  </label>
                )}
              </>
            )}
          </div>
        )}

        {err && <div className={styles.modalError} role="alert">{err}</div>}
        <div className={styles.modalActions}>
          <button className={styles.gridMiniBtn} onClick={onClose}>Cancel</button>
          <button className={styles.gridPageBtn} disabled={!canApply} onClick={apply}>
            {survivor && legacy
              ? `Merge ${label(legacy)} into ${label(survivor)}`
              : 'Merge'}
          </button>
        </div>
      </div>
    </div>
  );
}
